import { CircularProgress } from "@material-ui/core";
import { useWatch } from "react-hook-form";
import money from "../../utilities/money";

export default function PreviewSidebar({ isLoadingPreview, previewInfo, isUpdateMode }) {
  const [companyName, billing, seats, enrichment, dedicatedCsm] = useWatch({
    name: ["companyName", "billing", "seats", "enrichment", "dedicatedCsm"],
  });

  const getIntervalLabel = () => {
    const opt = billing?.intervalOption;
    switch (opt) {
      case "monthly":
        return "Monthly";
      case "quarterly":
        return "Quarterly (every 3 months)";
      case "semiAnnually":
        return "Semi-annually (every 6 months)";
      case "yearly":
        return "Yearly";
      case "2year":
        return "2 year (every 2 years)";
      case "3year":
        return "3 year (every 3 years)";
      default:
        return `${billing?.interval || "year"} × ${billing?.intervalCount || 1}`;
    }
  };

  return (
    <aside>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-96">
        {isLoadingPreview ? (
          <div className="flex items-center justify-center min-h-96">
            <CircularProgress style={{ color: "gray" }} />
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-gray-900">Summary</h3>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                Preview
              </span>
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Company
                </div>
                <div className="text-sm font-medium text-gray-900">{companyName || "—"}</div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Billing
                </div>
                {billing?.trial && billing?.trial.enabled ? (
                  <>
                    <div className="text-sm font-medium text-gray-900">
                      Trial until {billing.trial.expiresOn}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Trial enabled — billing frequency and scheduling are ignored.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm font-medium text-gray-900">{getIntervalLabel()}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {billing?.chargeAutomatically
                        ? "Auto-charge enabled"
                        : "Send Invoice Enabled"}
                    </div>
                    {isUpdateMode && billing?.resetCycle && (
                      <div className="text-xs text-amber-600 mt-1 font-medium">
                        ⚠ Billing cycle will be reset
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Seats
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-gray-900">
                    {previewInfo?.seats?.quantity}
                  </span>
                  <span className="text-sm text-gray-500">paid</span>
                </div>
                {seats.freeSeatsEnabled && (
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-lg font-semibold text-gray-700">
                      {previewInfo?.freeSeats?.quantity}
                    </span>
                    <span className="text-xs text-gray-500">free</span>
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-2">
                  {seats.hasCustomPricing ? (
                    <>
                      Price per seat:{" "}
                      {money.formatDollars(money.centsToDollars(previewInfo?.seats?.unitAmount))}
                    </>
                  ) : (
                    <>Price per seat: (server default)</>
                  )}
                </div>
              </div>

              {enrichment.enabled && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Enrichment
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">
                      {enrichment.credits}
                    </span>
                    <span className="text-sm text-gray-500">credits</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    {enrichment.hasCustomPricing ? (
                      <>
                        Price per credit:{" "}
                        {money.formatDollars(
                          money.centsToDollars(previewInfo?.credits?.unitAmount),
                        )}
                      </>
                    ) : (
                      <>Price per credit: (server default)</>
                    )}
                  </div>
                  <div className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-xs font-medium text-gray-700 mt-2">
                    {enrichment.level}
                  </div>
                </div>
              )}

              {enrichment.enabled && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Features
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {enrichment.options.hasListEnrichment && (
                      <div className="flex items-center gap-2 text-xs text-gray-700">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        List enrichment
                      </div>
                    )}
                    {enrichment.options.hasFreeListEnrichment && (
                      <div className="flex items-center gap-2 text-xs text-gray-700">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Free list enrichment
                      </div>
                    )}
                    {enrichment.options.hasEventIntelligence && (
                      <div className="flex items-center gap-2 text-xs text-gray-700">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Event intelligence
                      </div>
                    )}
                  </div>
                </div>
              )}

              {dedicatedCsm.enabled && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Dedicated CSM
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">
                      {dedicatedCsm.hasCustomPricing
                        ? money.formatDollars(
                          money.centsToDollars(previewInfo?.dedicatedCsm?.totalAmount),
                        )
                        : "—"}
                    </span>
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Totals per year
                </div>
                <div className="mt-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <div>Seats</div>
                    <div>
                      {money.formatDollars(money.centsToDollars(previewInfo?.seats?.yearlyTotalAmount))}
                    </div>
                  </div>

                  {enrichment.enabled && (
                    <div className="flex justify-between text-sm text-gray-600 mt-1">
                      <div>Enrichment</div>
                      <div>
                        {money.formatDollars(
                          money.centsToDollars(previewInfo?.credits?.yearlyTotalAmount),
                        )}
                      </div>
                    </div>
                  )}

                  {dedicatedCsm.enabled && (
                    <div className="flex justify-between text-sm text-gray-600 mt-1">
                      <div>Dedicated CSM</div>
                      <div>
                        {money.formatDollars(
                          money.centsToDollars(previewInfo?.dedicatedCsm?.yearlyTotalAmount),
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between text-base font-semibold text-gray-900 mt-3 border-t pt-3">
                    <div>Total per year</div>
                    <div>{money.formatDollars(money.centsToDollars(previewInfo?.yearlyTotalAmount))}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
